import { ExamineOutputService } from '../../_services';
import { parseTemperatureGridText } from '@/utils/thermal/parseTemperatureGridText';

const ThermalDataHolder = function  () {
  this.dataMap = [];
  this.maxValue = 0;
  this.minValue = 100;
  this.average = 0;
  this.loading = false;
  this.loadCompleted = false;
  this.currentThermalImageId = null;
  /** getThermalData 그리드 실제 크기 (픽셀 인덱스 상한용) */
  this.mapPixelWidth = 0;
  this.mapPixelHeight = 0;
  /** reshape | lines */
  this.parseMode = null;

  /**
   * @param {number|string} thermalImageId
   * @param {{ expectedWidth?: number, expectedHeight?: number, imgWidth?: number, imgHeight?: number }} [options]
   *   IR 이미지 가로/세로. 있으면 flat 값을 width×height 로 reshape (줄바꿈 stride 오류 방지).
   */
  ThermalDataHolder.prototype.load = function (thermalImageId, options = {}){
    const expectedWidth = options.expectedWidth ?? options.imgWidth;
    const expectedHeight = options.expectedHeight ?? options.imgHeight;
    console.log('[THERMAL DATA HOLDER] load 시작', {
      thermalImageId,
      expectedWidth,
      expectedHeight
    });

    if (this.currentThermalImageId === thermalImageId && this.loadCompleted) {
      console.log('[THERMAL DATA HOLDER] 이미 로드된 이미지 재사용:', thermalImageId);
      return Promise.resolve({
        min: this.minValue,
        max: this.maxValue,
        average: this.average
      });
    }

    this.loading = true;
    this.loadCompleted = false;

    this.clearData();
    this.loading = true;
    this.currentThermalImageId = thermalImageId;

    return ExamineOutputService.getThermalData(thermalImageId)
      .then((thermalData) => {
        console.log('[THERMAL DATA HOLDER] API 응답 받음, 데이터 길이:', thermalData?.length);
        console.log('[THERMAL DATA HOLDER] 데이터 샘플:', thermalData?.substring(0, 200));

        const parsed = parseTemperatureGridText(thermalData, {
          expectedWidth,
          expectedHeight
        });

        if (!parsed) {
          console.error('[THERMAL DATA HOLDER] 데이터가 충분하지 않음 또는 파싱 실패');
          this.loadCompleted = false;
          this.loading = false;
          throw new Error('Thermal data is insufficient');
        }

        this.dataMap = parsed.dataMap;
        this.minValue = parsed.min;
        this.maxValue = parsed.max;
        this.average = parsed.average;
        this.mapPixelWidth = parsed.width;
        this.mapPixelHeight = parsed.height;
        this.parseMode = parsed.mode;
        this.loadCompleted = true;
        this.loading = false;
        this.currentThermalImageId = thermalImageId;

        console.log('[THERMAL DATA HOLDER] 데이터 처리 완료:', {
          thermalImageId,
          parseMode: this.parseMode,
          dataMapLength: this.dataMap.length,
          row0Length: this.dataMap[0]?.length,
          mapPixelWidth: this.mapPixelWidth,
          mapPixelHeight: this.mapPixelHeight,
          expectedWidth,
          expectedHeight,
          valueCount: parsed.valueCount,
          minValue: this.minValue,
          maxValue: this.maxValue,
          average: this.average,
          sampleRow0_0_7: this.dataMap[0]?.slice(0, 8),
          sampleMid:
            this.dataMap[Math.floor(this.dataMap.length / 2)]?.slice(0, 8),
          loadCompleted: this.loadCompleted,
          loading: this.loading
        });

        if (
          Number.isFinite(Number(expectedWidth)) &&
          Number.isFinite(Number(expectedHeight)) &&
          (this.mapPixelWidth !== Number(expectedWidth) ||
            this.mapPixelHeight !== Number(expectedHeight))
        ) {
          console.warn('[THERMAL DATA HOLDER] 격자 크기 ≠ IR 크기', {
            grid: { w: this.mapPixelWidth, h: this.mapPixelHeight },
            ir: { w: expectedWidth, h: expectedHeight },
            parseMode: this.parseMode
          });
        }

        return {
          min: this.minValue.toFixed(1),
          max: this.maxValue.toFixed(1),
          average: this.average.toFixed(1)
        };
      },
      (error) => {
        console.error('[THERMAL DATA HOLDER] thermal data holder execution failed', error);
        this.loadCompleted = false;
        this.loading = false;
        throw error;
      });
  },

  ThermalDataHolder.prototype.clearData = function(){
    console.log('[THERMAL DATA HOLDER] 메모리 정리 시작');
    this.dataMap = [];
    this.maxValue = 0;
    this.minValue = 100;
    this.average = 0;
    this.loading = false;
    this.loadCompleted = false;
    this.currentThermalImageId = null;
    this.mapPixelWidth = 0;
    this.mapPixelHeight = 0;
    this.parseMode = null;

    if (typeof global !== 'undefined' && global.gc) {
      global.gc();
    }

    console.log('[THERMAL DATA HOLDER] 메모리 정리 완료');
  },

  ThermalDataHolder.prototype.isLoadingCompleted = function(){
    return !this.loading && this.loadCompleted;
  },

  ThermalDataHolder.prototype.getPointValue = function(x, y){
    if (!this.isLoadingCompleted()) {
      return null;
    }
    const row = this.dataMap[y];
    if (!row) {
      return null;
    }
    const v = row[x];
    if (v === undefined || v === null || Number.isNaN(v)) {
      return null;
    }
    return v;
  },

  ThermalDataHolder.prototype.getMinMaxAverage = function(topLeftX, topLeftY, bottomRightX, bottomRightY){

    if(this.isLoadingCompleted()){
      let minTemp = 100.0;
      let maxTemp = 0.0;
      let tempCount = 0;
      let tempSum = 0.0;
      const maxRow = Math.max(0, (this.mapPixelHeight || this.dataMap.length) - 1);
      const maxCol = Math.max(0, (this.mapPixelWidth || (this.dataMap[0] && this.dataMap[0].length) || 0) - 1);
      const y0 = Math.max(Math.floor(topLeftY), 0);
      const y1 = Math.min(Math.floor(bottomRightY), maxRow);
      const x0 = Math.max(Math.floor(topLeftX), 0);
      const x1 = Math.min(Math.floor(bottomRightX), maxCol);
      for (let iy = y0; iy <= y1; iy++) {
        const xTempArr = this.dataMap[iy];
        if (!xTempArr) continue;
        for (let ix = x0; ix <= x1; ix++) {
          const tempVal = xTempArr[ix];
          if (tempVal === undefined || tempVal === null || Number.isNaN(tempVal)) {
            continue;
          }
          tempCount += 1;
          tempSum += tempVal;
          if (tempVal > maxTemp) {
            maxTemp = tempVal;
          }
          if (tempVal < minTemp) {
            minTemp = tempVal;
          }
        }
      }

      if (tempCount === 0) {
        return null;
      }

      return {
        min: minTemp.toFixed(1),
        max: maxTemp.toFixed(1),
        avr: (tempSum / tempCount).toFixed(1)
      };
    }else{
      return null;
    }
  };
};

export default ThermalDataHolder;
